/**
 * History router. Patterns are literal segments plus `:name` captures; the
 * first match wins, so order the table from most to least specific. The worker
 * (and Vite) serve the SPA for any unmatched path, so deep links work.
 */

export type RouteParams = Record<string, string>;

export interface PageContext {
  params: RouteParams;
  query: URLSearchParams;
  path: string;
  host: HTMLElement;
  navigate: Navigate;
}

/** Pages return a teardown for anything they attached outside `host`. */
export type Teardown = (() => void) | void;

export interface Route {
  pattern: string;
  render: (ctx: PageContext) => Teardown | Promise<Teardown>;
}

export interface NavigateOptions {
  replace?: boolean;
  /** Keep the current `#…` view state (used by the year switcher). */
  keepHash?: boolean;
}

export type Navigate = (path: string, options?: NavigateOptions) => void;

/**
 * Match one pattern against a path. Returns captured params, or null — also
 * for a segment whose percent-escapes do not decode: `/c/%E0%A4%A` is not an
 * address anything answers to, and it should land on the not-found page rather
 * than throw out of the router.
 */
export function matchPath(pattern: string, path: string): RouteParams | null {
  const p = split(pattern);
  const s = split(path);
  if (p.length !== s.length) return null;
  const params: RouteParams = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const value = s[i]!;
    if (seg.startsWith(':')) {
      if (!value) return null;
      try {
        params[seg.slice(1)] = decodeURIComponent(value);
      } catch {
        return null;
      }
    } else if (seg !== value) {
      return null;
    }
  }
  return params;
}

function split(path: string): string[] {
  return path.replace(/^\/+|\/+$/g, '').split('/').filter((s) => s.length > 0);
}

export function matchRoutes(
  routes: Route[],
  path: string,
): { route: Route; params: RouteParams } | null {
  for (const route of routes) {
    const params = matchPath(route.pattern, path);
    if (params) return { route, params };
  }
  return null;
}

export interface RouterOptions {
  routes: Route[];
  host: HTMLElement;
  notFound: (ctx: PageContext) => Teardown | Promise<Teardown>;
  onError?: (error: unknown) => void;
}

export class Router {
  private readonly options: RouterOptions;
  private teardown: Teardown = undefined;
  /** Guards against a slow page finishing after the user has moved on. */
  private generation = 0;

  constructor(options: RouterOptions) {
    this.options = options;
  }

  readonly navigate: Navigate = (path, opts = {}) => {
    const url = new URL(path, location.href);
    if (opts.keepHash && !url.hash) url.hash = location.hash;
    const next = url.pathname + url.search + url.hash;
    if (opts.replace) history.replaceState(null, '', next);
    else history.pushState(null, '', next);
    void this.render();
  };

  start(): void {
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('click', this.onClick);
    void this.render();
  }

  destroy(): void {
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('click', this.onClick);
    this.runTeardown();
  }

  private readonly onPopState = (): void => {
    void this.render();
  };

  /** Same-origin plain links become navigations; everything else is left alone. */
  private readonly onClick = (event: MouseEvent): void => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
    if (anchor.getAttribute('rel')?.includes('external')) return;
    const url = new URL(href, location.href);
    if (url.origin !== location.origin) return;
    if (url.pathname === location.pathname && url.hash) return; // in-page hash link
    event.preventDefault();
    this.navigate(url.pathname + url.search + url.hash);
  };

  private runTeardown(): void {
    const fn = this.teardown;
    this.teardown = undefined;
    try {
      fn?.();
    } catch (err) {
      this.options.onError?.(err);
    }
  }

  async render(): Promise<void> {
    const generation = ++this.generation;
    this.runTeardown();
    const path = location.pathname;
    const ctx: PageContext = {
      params: {},
      query: new URLSearchParams(location.search),
      path,
      host: this.options.host,
      navigate: this.navigate,
    };
    const hit = matchRoutes(this.options.routes, path);
    const render = hit ? hit.route.render : this.options.notFound;
    if (hit) ctx.params = hit.params;

    this.options.host.replaceChildren();
    try {
      const teardown = await render(ctx);
      if (generation !== this.generation) {
        // Navigated away while this page was loading: undo it immediately.
        teardown?.();
        return;
      }
      this.teardown = teardown;
    } catch (err) {
      if (generation !== this.generation) return;
      this.options.onError?.(err);
    }
  }
}
